import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, DropdownMenu } from '@3plearning/react-components';
import { useDispatch, useSelector } from 'react-redux';
import Isvg from 'react-inlinesvg';
import { FilterListWrapper, FilterList, FiltersBtnWrapper, ContextModalContent, FiltersBtnText } from './styled';
import {
  getCurriculumSelectList,
  getSelectedGradesSelectList,
  getSelectedPublicationCourses,
  hideGradeList,
  sortOld
} from './course-filters-util';
import { FilterOutlineIcon, DownArrow } from '../../helpers/icon-dictionary';
import ContextModal from '../context-modal';
import OutsideClickHandler from '../../helpers/hoc/close/outside-click-handler';
import SearchableDropDownList from '../searchable-dropdownlist';
import { applyFilter, showHideSearchPanel, updateStrand } from '../../containers/upf-assign/upf-assign-actions';
import ErrorBoundary from '../../containers/error-boundary';
import GroupedOptionsDropdown from '../grouped-options-dropdown/grouped-options-dropdown';
import LocalStorage from '../../helpers/local-storage';

const LAST_USED_PUBLICATION = 'LAST_USED_PUBLICATION';
const LAST_USED_COURSE = 'LAST_USED_COURSE';
const LAST_USED_GRADE = 'LAST_USED_GRADE';

const CourseFilters = (props) => {
  const { publications = [], i18n, countryCode } = props;
  const [publicationList, setPublication] = useState([]);
  const [courses, setCourses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [selectedPublication, setselectedPublication] = useState({ label: '', value: '' });
  const [selectedGrade, setSelectedGrade] = useState({ label: '', value: '' });
  const [selectedCourse, setselectedCourse] = useState({ label: '', value: '' });
  const showSearchPanel = useSelector(state => state.UPFAssignReducer.showSearchPanel);
  const topics = useSelector(state => state.UPFAssignReducer.topics);
  const [hiddenDropDownAssessments, setHiddenDropDownAssessments] = useState([null]);
  const COUNTRY_CODE = countryCode;

  const dispatch = useDispatch();
  const [showFilters, setShowFilters] = useState(false);

  const updateStrands = (actionType, id, checked, searchTerm) => {
    dispatch(updateStrand(actionType, id, checked, searchTerm));
  };

  const applyChanges = () => {
    dispatch(applyFilter());
  };

  const hideShowSearchPanel = (status) => {
    dispatch(showHideSearchPanel(status));
  };

  const LAST_PUBLICATION_KEY = `${COUNTRY_CODE}_${LAST_USED_PUBLICATION}`;
  const LAST_COURSE_KEY = `${COUNTRY_CODE}_${LAST_USED_COURSE}`;
  const LAST_GRADE_KEY = `${COUNTRY_CODE}_${LAST_USED_GRADE}`;

  const onCourseSelect = (selectedOption = {}) => {
    LocalStorage.setItem(LAST_COURSE_KEY, selectedOption);
    setselectedCourse(selectedOption);
    props.genericCourses(selectedOption);
  };

  const onGradeSelect = (selectedOption = {}, isInitialLoad = false) => {
    const courseList = sortOld(getSelectedPublicationCourses(selectedOption.courses, selectedOption.value || null));
    setCourses(courseList);
    setSelectedGrade(selectedOption);
    LocalStorage.setItem(LAST_GRADE_KEY, selectedOption);

    const lastUsedCourse = LocalStorage.getItem(LAST_COURSE_KEY);
    const firstCourse = courseList && courseList[0];
    if (lastUsedCourse === '' ||
      Object.keys(lastUsedCourse).length === 0 ||
      !isInitialLoad) {
      const lastUsedPublication = LocalStorage.getItem(LAST_PUBLICATION_KEY) || {};
      LocalStorage.setItem(LAST_PUBLICATION_KEY, lastUsedPublication);
      setselectedCourse(firstCourse);
      onCourseSelect(firstCourse);
    } else {
      const latestCourse = courseList.filter(course => course?.course?.id === lastUsedCourse?.course?.id);
      if (latestCourse && latestCourse.length) {
        setselectedCourse(latestCourse[0]);
        onCourseSelect(latestCourse[0]);
      } else {
        setselectedCourse(firstCourse);
        onCourseSelect(firstCourse);
      }
    }
  };

  const onMasterCourseChange = (selectedOption = {}, isInitialLoad = false) => {
    const rawGradeList = getSelectedGradesSelectList(selectedOption.publications, selectedOption.value || null);
    const gradeList = hideGradeList(rawGradeList, hiddenDropDownAssessments);
    LocalStorage.setItem(LAST_PUBLICATION_KEY, selectedOption);

    const lastUsedGrade = LocalStorage.getItem(LAST_GRADE_KEY);
    const hasLastUsedGrade = lastUsedGrade !== '' && Object.keys(lastUsedGrade).length;

    // the stored grade holds a stale `courses` snapshot, so re-resolve against the fresh list
    const restoredGrade = (isInitialLoad && hasLastUsedGrade)
      ? gradeList.find(grade => grade.value === lastUsedGrade.value)
      : null;

    // stored grade is gone - drop it and the assessment held under it
    if (isInitialLoad && hasLastUsedGrade && !restoredGrade) {
      LocalStorage.removeItem(LAST_GRADE_KEY);
      LocalStorage.removeItem(LAST_COURSE_KEY);
    }

    const firstGradeOption = restoredGrade || (gradeList && gradeList[0]);
    setSelectedGrade(firstGradeOption);
    onGradeSelect(firstGradeOption, isInitialLoad);

    setGrades(gradeList);
    setselectedPublication(selectedOption);
  };

  useEffect(() => {
    setHiddenDropDownAssessments(JSON.parse(window._env_.HideDropDownAssessments));
  }, []);

  useEffect(() => {
    if (publications.length > 0) {
      const selectList = getCurriculumSelectList(publications) || [];
      let lastUsedPublication = LocalStorage.getItem(LAST_PUBLICATION_KEY);
      const isInitialLoad = true;

      if (lastUsedPublication === '' || !Object.keys(lastUsedPublication).includes('publications')) {
        const firstPublication = selectList[0].groupOptions[0];
        LocalStorage.setItem(LAST_PUBLICATION_KEY, firstPublication);
        setselectedPublication(firstPublication);
        onMasterCourseChange(firstPublication, isInitialLoad);
        setPublication(selectList);
        return;
      }
      selectList.forEach((l) => {
        l.groupOptions.forEach((o) => {
          if (o.value === lastUsedPublication.value) {
            lastUsedPublication = o;
          }
        });
      });

      setPublication(selectList);
      setselectedPublication(lastUsedPublication);
      onMasterCourseChange(lastUsedPublication, isInitialLoad);
    }
  }, [publications]);

  const buttonHeight = 35;
  const contentTop = buttonHeight + 5;

  return (<FiltersBtnWrapper id="course-filters-wrapper" height={buttonHeight}>
    <Button
      className="filters-btn"
      icon={FilterOutlineIcon}
      onClick={() => { setShowFilters(!showFilters); }}
      inverse
      text={
        <FiltersBtnText key="filters-btn-text">
          <span>{i18n.t`Curriculum/Activity Filters`}</span>
          <Isvg src={DownArrow} cacheGetRequests />
        </FiltersBtnText>
      }
    />
    <OutsideClickHandler forButton="course-filters-wrapper" callback={() => { setShowFilters(false); }}>
      <ContextModal
        show={showFilters}
        positionX={0}
        positionY={contentTop}
        modalWidth={570}
        positionArrowFixed={135}
        fixedParent
      >
        <ContextModalContent>
          <FilterListWrapper>
            <FilterList>
              <label htmlFor="publications">{i18n.t`Curriculum`}</label>
              <GroupedOptionsDropdown
                className="publications"
                key="publications"
                options={publicationList}
                onSelectOption={onMasterCourseChange}
                selectedOption={selectedPublication}
                data-name="publications"
              />
            </FilterList>
            <FilterList>
              <label htmlFor="grades">{i18n.t`Grade/year`}</label>
              <DropdownMenu
                className="grades"
                key="grades"
                options={grades}
                onSelectOption={onGradeSelect}
                selectedOption={selectedGrade}
                disabled={!selectedPublication.value}
                data-name="grades"
              />
            </FilterList>
            <FilterList>
              <label htmlFor="courses">{i18n.t`Assessment`}</label>
              <DropdownMenu
                className="courses"
                key="courses"
                options={courses}
                onSelectOption={onCourseSelect}
                selectedOption={selectedCourse}
                disabled={!selectedPublication.value}
                data-name="courses"
              />
            </FilterList>
          </FilterListWrapper>
          <div>
            <OutsideClickHandler
              callback={() => showSearchPanel && hideShowSearchPanel(false)}
              forButton="SearchableDropDownList">
              <ErrorBoundary>
                <SearchableDropDownList
                  id="SearchableDropDownList"
                  topics={topics}
                  updateStrands={updateStrands}
                  applyFilter={applyChanges}
                  showSearchPanel={showSearchPanel}
                  showHideSearchPanel={hideShowSearchPanel}
                  i18n={i18n} />
              </ErrorBoundary>
            </OutsideClickHandler>
          </div>
        </ContextModalContent>
      </ContextModal>
    </OutsideClickHandler>
  </FiltersBtnWrapper>);
};

CourseFilters.propTypes = {
  countryCode: PropTypes.string.isRequired,
  publications: PropTypes.array,
  genericCourses: PropTypes.func,
  i18n: PropTypes.object
};

export default CourseFilters;
